import { useCallback, useMemo, useState } from "react";
import { drawingsForLevel, getAnimal } from "../data/animals";
import { shapes } from "../data/shapes";
import { masterpieces } from "../data/masterpieces";
import { allFlags } from "../data/worldcup";
import type { Animal, Level } from "../data/animals";
import { downloadColoringPdf } from "../lib/coloringPdf";
import { AnimalCard } from "./AnimalCard";
import { Brand } from "./Brand";
import { LevelSelector } from "./LevelSelector";
import { useVoiceControl, VoiceButton } from "../voice/VoiceProvider";
import { SoundButton } from "../sound/SoundProvider";
import { SpeakButton } from "../speech/SpeakProvider";
import { AutoPlayButton } from "../settings/AutoPlayProvider";
import { ThemeButton } from "../theme/ThemeButton";
import { applyTheme } from "../theme/theme";
import { scrollPage } from "../lib/scroll";
import { heardAny } from "../voice/match";
import { TimeLimitSettings } from "../parental/TimeLimitSettings";
import { useTimeLimit } from "../parental/TimeLimitProvider";
import { useLocale, useT } from "../i18n/LocaleProvider";
import { aliasesFor, localizeAnimal } from "../i18n/localize";
import { ParentsNote, FlagsDisclaimer } from "./FooterProse";
import { COFFEE_URL } from "../lib/support";
import { staticPath } from "../i18n/staticPages";


// Under "Pick something to draw", the difficulty comes first; then (for Easy)
// a category sub-filter narrows the grid. Shapes and Paintings are Easy
// categories now — more categories will be added going forward. Only Easy has
// sub-categories today, so the row shows only there; Medium/Hard open straight
// to their drawings.
type Category = "all" | "shapes" | "paintings";
const CATEGORIES: {
  key: Category;
  icon: string;
  label: "catAll" | "catShapes" | "catPaintings";
  aria: "catAllAria" | "catShapesAria" | "catPaintingsAria";
}[] = [
  { key: "all", icon: "✨", label: "catAll", aria: "catAllAria" },
  { key: "shapes", icon: "🔷", label: "catShapes", aria: "catShapesAria" },
  { key: "paintings", icon: "🖼️", label: "catPaintings", aria: "catPaintingsAria" },
];

interface Props {
  onPick: (animal: Animal) => void;
  /** Ids of animals already finished — shown with a green check. */
  completed: Set<string>;
  level: Level;
  onLevelChange: (level: Level) => void;
  onOpenShapes: () => void;
  onOpenFacts: () => void;
  onOpenWhy: () => void;
  onOpenPaintings: () => void;
  onOpenFlags: () => void;
  onOpenTrophies: () => void;
  onOpenPrivacy: () => void;
  onOpenContact: () => void;
  onOpenTerms: () => void;
}

export function HomePage({
  onPick,
  completed,
  level,
  onLevelChange,
  onOpenShapes,
  onOpenFacts,
  onOpenWhy,
  onOpenPaintings,
  onOpenFlags,
  onOpenTrophies,
  onOpenPrivacy,
  onOpenContact,
  onOpenTerms,
}: Props) {
  const { locale } = useLocale();
  const t = useT();
  const [category, setCategory] = useState<Category>("all");
  // Only Easy has sub-categories for now; Medium/Hard show their grid directly.
  const showCategories = level === 5;

  // Switching difficulty resets the category, so Medium/Hard (no sub-categories
  // yet) never get stuck showing an empty "Shapes"/"Painting" filter.
  const handleLevelChange = useCallback(
    (next: Level) => {
      setCategory("all");
      onLevelChange(next);
    },
    [onLevelChange],
  );

  const visible = useMemo(() => {
    const base =
      level !== 5
        ? drawingsForLevel(level)
        : category === "shapes"
          ? shapes
          : category === "paintings"
            ? masterpieces
            : // "All" (Easy) = the easy drawings plus the shape and painting sets.
              [...drawingsForLevel(5), ...shapes, ...masterpieces];
    return base.map((a) => localizeAnimal(a, locale));
  }, [level, category, locale]);

  const [printing, setPrinting] = useState(false);
  const [showTimeLimit, setShowTimeLimit] = useState(false);
  // "Fewer distractions": hide the footer and the Print-to-color button.
  const { fewScreen } = useTimeLimit();

  const handlePrint = useCallback(async () => {
    if (printing || visible.length === 0) return;
    setPrinting(true);
    try {
      // Upgrade easy drawings to their harder versions when available — the PDF
      // is for colouring on paper; the harder version has more detail to colour.
      // Also include all flags at the end.
      const forPdf = [
        ...visible.map((a) => {
          const lvl10 = getAnimal(`${a.id}-10`);
          return lvl10 ? localizeAnimal(lvl10, locale) : a;
        }),
        ...allFlags.map((f) => localizeAnimal(f, locale)),
      ];
      await downloadColoringPdf(forPdf, level);
    } finally {
      setPrinting(false);
    }
  }, [printing, visible, level, locale]);

  const onCommand = useCallback(
    (transcript: string): boolean => {
      if (heardAny(transcript, t.commands.up)) {
        scrollPage(-1);
        return true;
      }
      if (heardAny(transcript, t.commands.down)) {
        scrollPage(1);
        return true;
      }
      if (heardAny(transcript, t.commands.shapes)) {
        onOpenShapes();
        return true;
      }
      if (heardAny(transcript, t.commands.facts)) {
        onOpenFacts();
        return true;
      }
      if (heardAny(transcript, t.commands.why)) {
        onOpenWhy();
        return true;
      }
      if (heardAny(transcript, t.commands.paintings)) {
        onOpenPaintings();
        return true;
      }
      if (heardAny(transcript, t.commands.flags)) {
        onOpenFlags();
        return true;
      }
      if (heardAny(transcript, t.commands.trophies)) {
        onOpenTrophies();
        return true;
      }
      if (heardAny(transcript, t.commands.print)) {
        handlePrint();
        return true;
      }
      if (heardAny(transcript, t.commands.privacy)) {
        onOpenPrivacy();
        return true;
      }
      if (heardAny(transcript, t.commands.contact)) {
        onOpenContact();
        return true;
      }
      if (heardAny(transcript, t.commands.terms)) {
        onOpenTerms();
        return true;
      }
      if (heardAny(transcript, t.commands.dark)) {
        applyTheme("dark");
        return true;
      }
      if (heardAny(transcript, t.commands.light)) {
        applyTheme("light");
        return true;
      }
      for (const animal of visible) {
        if (heardAny(transcript, aliasesFor(animal, locale))) {
          onPick(animal);
          return true;
        }
      }
      return false;
    },
    [
      t,
      locale,
      onPick,
      onOpenShapes,
      onOpenFacts,
      onOpenWhy,
      onOpenPaintings,
      onOpenFlags,
      onOpenTrophies,
      onOpenPrivacy,
      onOpenContact,
      onOpenTerms,
      visible,
      handlePrint,
    ],
  );
  useVoiceControl(onCommand);

  return (
    <div className="home">
      <Brand />

      {/* The core idea, said plainly: the screen only teaches — the real drawing
          happens on paper. Reinforced so parents set kids up with pen + paper.
          Hidden in "Fewer distractions" mode so the child sees only drawings. */}
      <h2 className="sr-only">{t.paperNoteStrong}</h2>
      {!fewScreen && (
        <div className="paper-note" role="note">
          <span className="paper-note-icon" aria-hidden="true">
            ✏️📄
          </span>
          <span className="paper-note-text">
            <strong>{t.paperNoteStrong}</strong>
            {t.paperNoteRest}
          </span>
        </div>
      )}

      {/* Row 1: the "read & collect" picks — Why Draw, Fun Facts, My Stickers. */}
      <div className="pick-row info-picks">
        <button
          className="level-btn why-pick"
          onClick={onOpenWhy}
          aria-label={t.whyDrawAria}
        >
          <span className="level-icon" aria-hidden="true">
            🎨
          </span>
          <span className="level-label">{t.whyDraw}</span>
        </button>
        <button
          className="level-btn facts-pick"
          onClick={onOpenFacts}
          aria-label={t.funFactsAria}
        >
          <span className="level-icon" aria-hidden="true">
            💡
          </span>
          <span className="level-label">{t.funFacts}</span>
        </button>
        <button
          className="level-btn trophies-pick"
          onClick={onOpenTrophies}
          aria-label={t.myStickersAria}
        >
          <span className="level-icon" aria-hidden="true">
            🏆
          </span>
          <span className="level-label">{t.myStickers}</span>
        </button>
      </div>

      <h2 className="sr-only">{t.pickCta}</h2>
      {/* Call to action for the levels + collections picker below. */}
      <p className="subtitle pick-cta">{t.pickCta}</p>

      {/* The difficulty picker + its Easy sub-menu, grouped so the category tabs
          line up under the Easy button (this wrapper shrinks to the row width
          and stays centred, so both rows share the same left edge). */}
      <div className="picker">
      {/* Row 2: difficulty — Easy / Medium / Harder — then the Flags collection,
          split off by a vertical divider (as before). */}
      <div className="level-row">
        <LevelSelector level={level} onChange={handleLevelChange} />
        <div className="level-divider" aria-hidden="true" />
        <div className="pick-row">
          <button
            className="level-btn flags-pick"
            onClick={onOpenFlags}
            aria-label={t.flagsAria}
          >
            <span className="level-icon" aria-hidden="true">
              🏳️
            </span>
            <span className="level-label">{t.flags}</span>
          </button>
        </div>
      </div>

      {/* Row 3 (Easy only): the category sub-filter. Shapes & Paintings are Easy
          categories; "All" mixes the easy drawings with both sets. More
          categories will be added over time. */}
      {showCategories && (
        <div
          className="category-row"
          role="group"
          aria-label={t.filterByCategory}
        >
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              className={c.key === category ? "category-btn active" : "category-btn"}
              onClick={() => setCategory(c.key)}
              aria-pressed={c.key === category}
              aria-label={t[c.aria]}
            >
              <span className="category-icon" aria-hidden="true">
                {c.icon}
              </span>
              <span className="category-label">{t[c.label]}</span>
            </button>
          ))}
        </div>
      )}
      </div>

      {/* Visual divider between the "what to draw" picker above and the tool
          buttons below. */}
      <div className="section-divider" role="separator" aria-hidden="true" />

      <div className="control-bar">
        {/* Settings: voice, sound, theme and the grown-up time limit. */}
        <div className="control-group" role="group" aria-label={t.settings}>
          <span className="control-group-label">{t.settings}</span>
          <div className="control-group-btns">
            <VoiceButton />
            <SoundButton />
            <SpeakButton />
            <AutoPlayButton />
            <ThemeButton />
            <button
              className="facts-btn grownup-btn"
              onClick={() => setShowTimeLimit(true)}
              aria-label={t.forGrownUpsAria}
            >
              <svg className="grownup-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94L14.4 2.81a.5.5 0 0 0-.47-.41h-3.84c-.24 0-.43.17-.47.41L9.25 5.35c-.59.24-1.13.56-1.62.94l-2.39-.96a.49.49 0 0 0-.59.22L2.74 8.87c-.12.22-.07.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94 0 .31.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61ZM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2Z"/></svg>{" "}
              {t.forGrownUps}
            </button>
          </div>
        </div>

        {/* Parent prep: print thick-line coloring sheets to draw/colour on paper.
            Hidden in "Fewer distractions" mode so the child sees only drawings. */}
        {!fewScreen && (
          <>
            <div className="control-group-sep" aria-hidden="true" />
            <div
              className="control-group"
              role="group"
              aria-label={t.printGroupAria}
            >
              <div className="control-group-btns">
                <button
                  className="print-cta"
                  onClick={handlePrint}
                  disabled={printing || visible.length === 0}
                >
                  {printing ? t.printMaking : t.printCta}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showTimeLimit && <TimeLimitSettings onClose={() => setShowTimeLimit(false)} />}

      {/* Up/down scroll controls on the left, centred at the drawing level. */}
      <div className="side-scroll">
        <button
          className="scroll-btn"
          onClick={() => scrollPage(-1)}
          aria-label={t.scrollUp}
        >
          ▲
        </button>
        <button
          className="scroll-btn"
          onClick={() => scrollPage(1)}
          aria-label={t.scrollDown}
        >
          ▼
        </button>
      </div>

      {visible.length === 0 ? (
        <p className="empty-state">
          More drawings for this level are coming soon! 🎨✨
          <br />
          Try the 🌱 Easy level for now.
        </p>
      ) : (
        <div className="card-grid">
          {visible.map((animal) => (
            <AnimalCard
              key={animal.id}
              animal={animal}
              done={completed.has(animal.id)}
              onClick={() => onPick(animal)}
            />
          ))}
        </div>
      )}

      {!fewScreen && (
      <footer className="home-footer">
        <a className="footer-link" href={staticPath("privacy/", locale)} onClick={(e) => { e.preventDefault(); onOpenPrivacy(); }}>
          {t.footerPrivacy}
        </a>
        <span className="footer-dot" aria-hidden="true">
          ·
        </span>
        <a className="footer-link" href={staticPath("contact/", locale)} onClick={(e) => { e.preventDefault(); onOpenContact(); }}>
          {t.footerHello}
        </a>
        <span className="footer-dot" aria-hidden="true">
          ·
        </span>
        <a className="footer-link" href={staticPath("terms/", locale)} onClick={(e) => { e.preventDefault(); onOpenTerms(); }}>
          {t.footerRules}
        </a>
        <span className="footer-dot" aria-hidden="true">
          ·
        </span>
        {/* The About story is a static, crawlable page (dist/about/ and
            dist/fr/about/) shared with search engines — a plain link, not an
            in-app route. */}
        <a className="footer-link" href={staticPath("about/", locale)}>
          {t.footerAbout}
        </a>
        <span className="footer-dot" aria-hidden="true">
          ·
        </span>
        <a
          className="footer-link"
          href="https://www.youtube.com/@learn2drawkids"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t.youtubeChannel}
        </a>

        {/* Optional support button (for grown-ups) — opens Stripe's hosted
            checkout in a new tab. No keys or backend involved. Also lives in the
            For grown-ups menu; this is the footer copy. */}
        <div className="coffee-row">
          <a
            className="coffee-btn"
            href={COFFEE_URL}
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            {t.coffeeBtn}
          </a>
          <span className="coffee-note">{t.coffeeNote}</span>
        </div>

        <ParentsNote />

        <p className="footer-love">
          <strong className="footer-brand">Learn 2 Draw</strong>
          {t.footerLove}
        </p>
        <FlagsDisclaimer onSayHello={onOpenContact} />
        <p className="footer-copyright">
          {t.footerCopyright(new Date().getFullYear())}
        </p>
      </footer>
      )}
    </div>
  );
}
